import { useState, useEffect, useCallback, useRef } from 'react';
import { MapPin, Plus, Trash2, Edit2, X, Navigation, Check, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

const GOOGLE_MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// ============= Places Autocomplete Input (New API) =============
const PlacesAutocompleteInput = ({ placeholder, className, onPlaceSelect, value, onChange, 'data-testid': testId }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [localVal, setLocalVal] = useState(value || '');
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => { setLocalVal(value || ''); }, [value]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchSuggestions = async (input) => {
    if (!input || input.length < 3 || !window.google?.maps?.places?.AutocompleteSuggestion) {
      setSuggestions([]);
      return;
    }
    try {
      const { suggestions: results } = await window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input,
        includedRegionCodes: ['in'],
      });
      setSuggestions(results || []);
      setShowDropdown((results || []).length > 0);
    } catch {
      setSuggestions([]);
    }
  };

  const handleInput = (e) => {
    const val = e.target.value;
    setLocalVal(val);
    if (onChange) onChange(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(val), 300);
  };

  const handleSelect = async (suggestion) => {
    setShowDropdown(false);
    try {
      const place = suggestion.placePrediction.toPlace();
      await place.fetchFields({ fields: ['location', 'formattedAddress', 'displayName', 'addressComponents'] });
      const name = place.displayName || place.formattedAddress || '';
      setLocalVal(name);
      if (onChange) onChange(name);
      if (onPlaceSelect) onPlaceSelect(place);
    } catch {
      // Fallback: use the suggestion text
      const text = suggestion.placePrediction?.text?.text || '';
      setLocalVal(text);
      if (onChange) onChange(text);
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <input
        type="text"
        value={localVal}
        onChange={handleInput}
        onFocus={() => suggestions.length > 0 && setShowDropdown(true)}
        placeholder={placeholder}
        className={className}
        data-testid={testId}
      />
      {showDropdown && suggestions.length > 0 && (
        <div className="absolute top-full left-0 right-0 bg-white border border-gold/30 shadow-xl rounded-sm max-h-48 overflow-y-auto z-50" data-testid="places-suggestions">
          {suggestions.map((s, i) => (
            <button
              key={i}
              type="button"
              onClick={() => handleSelect(s)}
              className="flex items-start space-x-2 w-full px-3 py-2.5 text-left hover:bg-neutral-warm transition-colors"
            >
              <MapPin className="w-3.5 h-3.5 text-gold flex-shrink-0 mt-0.5" />
              <span className="text-sm text-forest">{s.placePrediction?.text?.text || 'Unknown'}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ============= Map Picker (Primary Location Source) =============
const MapPicker = ({ onLocationSelect, initialLat, initialLng }) => {
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const [address, setAddress] = useState('');
  const center = { lat: initialLat || 28.6139, lng: initialLng || 77.209 };

  useEffect(() => {
    if (!window.google || !mapRef.current) return;

    const map = new window.google.maps.Map(mapRef.current, {
      center,
      zoom: 15,
      disableDefaultUI: true,
      zoomControl: true,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
    });
    mapInstanceRef.current = map;

    const marker = new window.google.maps.Marker({
      position: center,
      map,
      draggable: true,
    });
    markerRef.current = marker;

    marker.addListener('dragend', () => {
      const pos = marker.getPosition();
      reverseGeocode(pos.lat(), pos.lng());
    });

    map.addListener('click', (e) => {
      marker.setPosition(e.latLng);
      reverseGeocode(e.latLng.lat(), e.latLng.lng());
    });

    if (initialLat && initialLng) {
      reverseGeocode(initialLat, initialLng);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reverseGeocode = (lat, lng) => {
    if (!window.google) return;
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === 'OK' && results[0]) {
        setAddress(results[0].formatted_address);
        const components = results[0].address_components;
        const getComponent = (type) => components.find(c => c.types.includes(type))?.long_name || '';
        onLocationSelect({
          lat,
          lng,
          formatted_address: results[0].formatted_address,
          city: getComponent('locality') || getComponent('administrative_area_level_2'),
          state: getComponent('administrative_area_level_1'),
          pincode: getComponent('postal_code'),
        });
      }
    });
  };

  const handlePlaceSelect = (place) => {
    if (!place.location || !mapInstanceRef.current || !markerRef.current) return;
    const lat = place.location.lat();
    const lng = place.location.lng();
    mapInstanceRef.current.panTo({ lat, lng });
    mapInstanceRef.current.setZoom(17);
    markerRef.current.setPosition({ lat, lng });
    reverseGeocode(lat, lng);
  };

  const handleLocateMe = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const lat = pos.coords.latitude;
          const lng = pos.coords.longitude;
          if (mapInstanceRef.current && markerRef.current) {
            mapInstanceRef.current.panTo({ lat, lng });
            mapInstanceRef.current.setZoom(17);
            markerRef.current.setPosition({ lat, lng });
            reverseGeocode(lat, lng);
          }
        },
        () => toast.error('Could not get your location')
      );
    }
  };

  return (
    <div className="space-y-3">
      <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 flex items-start space-x-2">
        <MapPin className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-amber-800">Drag the pin to your exact location. City, State and Pincode will be filled automatically.</p>
      </div>
      <div className="flex items-center space-x-2">
        <PlacesAutocompleteInput
          placeholder="Search for your location..."
          className="flex-1 px-4 py-2.5 bg-ivory border border-gold/50 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-forest"
          onPlaceSelect={handlePlaceSelect}
          data-testid="map-search-input"
        />
        <button
          type="button" onClick={handleLocateMe}
          className="flex items-center space-x-1.5 px-3 py-2.5 text-sm text-forest border border-gold/50 rounded-sm hover:bg-forest/5 transition-colors whitespace-nowrap"
          data-testid="locate-me-button"
        >
          <Navigation className="w-4 h-4" />
          <span className="hidden sm:inline">My Location</span>
        </button>
      </div>
      <div ref={mapRef} className="w-full h-72 rounded-sm border border-gold/30" data-testid="map-picker" />
      {address && (
        <p className="text-xs text-muted-foreground bg-neutral-warm p-2 rounded-sm flex items-start space-x-1.5">
          <MapPin className="w-3 h-3 flex-shrink-0 mt-0.5" />
          <span>{address}</span>
        </p>
      )}
    </div>
  );
};

// ============= Address Form =============
const AddressForm = ({ address, onSave, onCancel }) => {
  const [form, setForm] = useState({
    label: address?.label || 'Home',
    name: address?.name || '',
    phone: address?.phone || '',
    address_line1: address?.address_line1 || '',
    address_line2: address?.address_line2 || '',
    city: address?.city || '',
    state: address?.state || '',
    pincode: address?.pincode || '',
    lat: address?.lat || null,
    lng: address?.lng || null,
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!form.name || !form.phone || !form.address_line1 || !form.city || !form.state || !form.pincode) {
      toast.error('Please fill all required fields');
      return;
    }
    setSaving(true);
    try {
      await onSave({ ...form, coordinates_source: 'manual' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4 bg-neutral-warm p-5 rounded-sm" data-testid="address-form">
      {/* Label */}
      <div className="flex space-x-2">
        {['Home', 'Office', 'Other'].map(label => (
          <button key={label} type="button"
            onClick={() => setForm({ ...form, label })}
            className={`px-4 py-2 text-xs uppercase tracking-wider rounded-sm border ${
              form.label === label ? 'bg-forest text-ivory border-forest' : 'border-gold/30 text-forest'
            }`}
            data-testid={`label-${label.toLowerCase()}`}>
            {label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="Full Name *" className="px-4 py-3 bg-ivory border border-gold/50 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-forest"
          data-testid="addr-name-input" />
        <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
          placeholder="Phone *" className="px-4 py-3 bg-ivory border border-gold/50 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-forest"
          data-testid="addr-phone-input" />
      </div>

      <input type="text" value={form.address_line1} onChange={(e) => setForm({ ...form, address_line1: e.target.value })}
        placeholder="Address Line 1 (House No., Street, Landmark) *"
        className="w-full px-4 py-3 bg-ivory border border-gold/50 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-forest"
        data-testid="addr-line1-input" />
      <input type="text" value={form.address_line2} onChange={(e) => setForm({ ...form, address_line2: e.target.value })}
        placeholder="Address Line 2 (optional)" className="w-full px-4 py-3 bg-ivory border border-gold/50 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-forest"
        data-testid="addr-line2-input" />

      <div className="grid grid-cols-3 gap-3">
        <input type="text" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
          placeholder="City *" className="w-full px-4 py-3 bg-ivory border border-gold/50 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-forest"
          data-testid="addr-city-input" />
        <input type="text" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })}
          placeholder="State *" className="w-full px-4 py-3 bg-ivory border border-gold/50 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-forest"
          data-testid="addr-state-input" />
        <input type="text" value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value })}
          placeholder="PIN Code *" className="w-full px-4 py-3 bg-ivory border border-gold/50 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-forest"
          data-testid="addr-pincode-input" />
      </div>

      <div className="flex space-x-3">
        <button type="button" onClick={handleSubmit} disabled={saving}
          className="bg-forest text-ivory px-6 py-3 rounded-none text-sm uppercase tracking-[0.1em] hover:bg-gold hover:text-forest transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="addr-save-button">
          {saving ? 'Saving...' : 'Save Address'}
        </button>
        <button type="button" onClick={onCancel}
          className="border border-forest text-forest px-6 py-3 rounded-none text-sm uppercase tracking-[0.1em] hover:bg-forest hover:text-ivory transition-colors">
          Cancel
        </button>
      </div>
    </div>
  );
};

// ============= Address Manager =============
const AddressManager = ({ onSelect, selectable = false, selectedId = null }) => {
  const [addresses, setAddresses] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);

  const fetchAddresses = useCallback(async () => {
    try {
      const response = await api.get('/addresses');
      setAddresses(response.data);
    } catch (error) {
      // User might not be logged in
    }
  }, []);

  useEffect(() => {
    fetchAddresses();
  }, [fetchAddresses]);

  const handleSave = async (formData) => {
    try {
      if (editingAddress) {
        await api.put(`/addresses/${editingAddress.address_id}`, formData);
        toast.success('Address updated');
      } else {
        await api.post('/addresses', formData);
        toast.success('Address saved');
      }
      setShowForm(false);
      setEditingAddress(null);
      fetchAddresses();
    } catch (error) {
      toast.error((() => {
        const d = error.response?.data?.detail;
        if (Array.isArray(d)) return d.map(e => e.msg).join(', ');
        if (typeof d === 'string') return d;
        return 'Failed to save address';
      })());
    }
  };

  const handleDelete = async (addressId) => {
    if (!window.confirm('Delete this address?')) return;
    try {
      await api.delete(`/addresses/${addressId}`);
      toast.success('Address deleted');
      fetchAddresses();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  return (
    <div className="space-y-4" data-testid="address-manager">
      {addresses.length > 0 && (
        <div className="space-y-3">
          {addresses.map((addr) => (
            <div
              key={addr.address_id}
              onClick={() => selectable && onSelect?.(addr)}
              className={`p-4 rounded-sm border transition-colors ${
                selectable ? 'cursor-pointer hover:border-forest' : ''
              } ${selectedId === addr.address_id ? 'border-forest bg-forest/5' : 'border-gold/30'}`}
              data-testid="saved-address-card"
            >
              <div className="flex justify-between items-start">
                <div className="flex items-start space-x-3">
                  {selectable && (
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                      selectedId === addr.address_id ? 'border-forest bg-forest' : 'border-gold/50'
                    }`}>
                      {selectedId === addr.address_id && <Check className="w-3 h-3 text-ivory" />}
                    </div>
                  )}
                  <div>
                    <div className="flex items-center space-x-2 mb-1">
                      <span className="text-xs uppercase tracking-wider bg-gold/20 text-forest px-2 py-0.5 rounded-sm font-medium">{addr.label}</span>
                      <span className="text-sm text-forest font-medium">{addr.name}</span>
                    </div>
                    <p className="text-sm text-muted-foreground">{addr.address_line1}{addr.address_line2 ? `, ${addr.address_line2}` : ''}</p>
                    <p className="text-sm text-muted-foreground">{addr.city}, {addr.state} - {addr.pincode}</p>
                    <p className="text-xs text-muted-foreground mt-1">{addr.phone}</p>
                    {addr.lat && addr.lng && (
                      <a href={`https://www.google.com/maps?q=${addr.lat},${addr.lng}`}
                        target="_blank" rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center space-x-1 text-xs text-forest/70 hover:text-gold mt-1 transition-colors"
                        data-testid="maps-link">
                        <MapPin className="w-3 h-3" /><span>View on Maps</span>
                      </a>
                    )}
                  </div>
                </div>
                <div className="flex space-x-1">
                  <button onClick={(e) => { e.stopPropagation(); setEditingAddress(addr); setShowForm(true); }}
                    className="p-1.5 text-forest hover:text-gold transition-colors" data-testid="edit-address-button">
                    <Edit2 className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); handleDelete(addr.address_id); }}
                    className="p-1.5 text-forest hover:text-red-500 transition-colors" data-testid="delete-address-button">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm ? (
        <AddressForm
          address={editingAddress}
          onSave={handleSave}
          onCancel={() => { setShowForm(false); setEditingAddress(null); }}
        />
      ) : (
        <button
          onClick={() => { setShowForm(true); setEditingAddress(null); }}
          className="w-full flex items-center justify-center space-x-2 border-2 border-dashed border-gold/40 p-4 rounded-sm text-sm text-forest hover:border-forest hover:bg-forest/5 transition-colors"
          data-testid="add-new-address-button"
        >
          <Plus className="w-4 h-4" />
          <span>Add New Address</span>
        </button>
      )}
    </div>
  );
};

export { AddressManager, AddressForm, MapPicker };
export default AddressManager;
